// src/pages/mairie/StatistiquesMairie.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';

export default function StatistiquesMairie() {
    const [stats, setStats] = useState({
        total_contribuables: 0,
        total_taxes: 0,
        total_collectes: 0,
        dette_jours: 0,
        dette_montant: 0,
        contribuables_en_regle: 0,
        contribuables_avec_dette: 0,
        par_quartier: [],
        par_recenseur: [],
        recharges_jour: 0,
        recharges_nb_jour: 0,
        recharges_mois: 0,
        recharges_nb_mois: 0
    });
    const [loading, setLoading] = useState(true);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${config.apiBase}/mairie/stats_globales.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                // ✅ S'assurer que les tableaux existent
                setStats({
                    ...data.stats,
                    par_quartier: data.stats.par_quartier || [],
                    par_recenseur: data.stats.par_recenseur || []
                });
            } else {
                toast.error(data.message || 'Erreur de chargement');
            }
        } catch (error) {
            console.error('Erreur fetchStats:', error);
            toast.error('Erreur de chargement des statistiques');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
            </div>
        );
    }

    // ✅ Vérifier que les tableaux existent avant d'utiliser .length
    const parQuartier = stats.par_quartier || [];
    const parRecenseur = stats.par_recenseur || [];

    return (
        <div className="max-w-6xl mx-auto p-6">
            <h1 className="text-2xl font-bold mb-6">📊 Statistiques</h1>

            {/* Cartes de statistiques */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow p-6 border-l-4 border-blue-600">
                    <p className="text-sm text-gray-500">Total contribuables</p>
                    <p className="text-3xl font-bold">{stats.total_contribuables || 0}</p>
                </div>
                <div className="bg-white rounded-xl shadow p-6 border-l-4 border-green-600">
                    <p className="text-sm text-gray-500">Total taxes</p>
                    <p className="text-3xl font-bold">{stats.total_taxes || 0}</p>
                </div>
                <div className="bg-white rounded-xl shadow p-6 border-l-4 border-purple-600">
                    <p className="text-sm text-gray-500">Total collectes</p>
                    <p className="text-3xl font-bold">{(stats.total_collectes || 0).toLocaleString()} F</p>
                </div>
                <div className="bg-white rounded-xl shadow p-6 border-l-4 border-red-600">
                    <p className="text-sm text-gray-500">Dettes</p>
                    <p className="text-3xl font-bold">{(stats.dette_montant || 0).toLocaleString()} F</p>
                    <p className="text-xs text-gray-400">{stats.dette_jours || 0} jours</p>
                </div>
            </div>

            {/* Deuxième ligne de stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow p-6 border-l-4 border-emerald-600">
                    <p className="text-sm text-gray-500">Contribuables en règle</p>
                    <p className="text-3xl font-bold">{stats.contribuables_en_regle || 0}</p>
                </div>
                <div className="bg-white rounded-xl shadow p-6 border-l-4 border-orange-600">
                    <p className="text-sm text-gray-500">Contribuables avec dette</p>
                    <p className="text-3xl font-bold">{stats.contribuables_avec_dette || 0}</p>
                </div>
                <div className="bg-white rounded-xl shadow p-6 border-l-4 border-indigo-600">
                    <p className="text-sm text-gray-500">Recharges aujourd'hui</p>
                    <p className="text-3xl font-bold">{(stats.recharges_jour || 0).toLocaleString()} F</p>
                    <p className="text-xs text-gray-400">{stats.recharges_nb_jour || 0} transactions</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Par quartier */}
                <div className="bg-white rounded-xl shadow p-6">
                    <h2 className="font-semibold mb-4">Par quartier</h2>
                    {parQuartier.length === 0 ? (
                        <p className="text-gray-500 text-sm">Aucune donnée</p>
                    ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {parQuartier.map((q, i) => (
                                <div key={i} className="flex justify-between border-b py-2">
                                    <span>{q.quartier || 'Non défini'}</span>
                                    <span className="font-semibold">{q.total || 0} contribuables</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Par recenseur */}
                <div className="bg-white rounded-xl shadow p-6">
                    <h2 className="font-semibold mb-4">Par recenseur</h2>
                    {parRecenseur.length === 0 ? (
                        <p className="text-gray-500 text-sm">Aucune donnée</p>
                    ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {parRecenseur.map((r, i) => (
                                <div key={i} className="flex justify-between border-b py-2">
                                    <span>{r.prenom || ''} {r.nom || ''}</span>
                                    <span className="font-semibold">{r.total || 0} contribuables</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Bouton rafraîchir */}
            <div className="mt-6 flex gap-4">
                <button
                    onClick={fetchStats}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                    🔄 Rafraîchir
                </button>
                <button
                    onClick={() => window.location.href = '/mairie/stats-collecteurs'}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition"
                >
                    📊 Performance des collecteurs
                </button>
            </div>
        </div>
    );
}